import React, { useState } from "react";
import useObjectInput from "../../hooks/useObjectInput";
import GOILoader from "./GOILoader";
import GOICompleteTree from "./GOICompleteTree";
import GOINode from "./GOINode";
import GOIFirstLevel from "./GOIFirstLevel";
import GOIErrors from "./GOIErrors";
import GOIRenderTabContent from "./GOIRenderTabContent";
import GOIHeaderSection from "./GOIHeaderSection";
import TreeWrapper from "./GOITree";
import GOIParentsHeader from "./GOIParentsHeader";
import "./index.css";

// The root component for the Generated Object Input. It renders the whole input form, and tree
const GOIRoot = () => {
  const {
    reqResponse,
    dataFetched,
    saving,
    editingChildrenIds,
    showCompleteTree,
    setShowCompleteTree,
    saveData,
    createRequestData,
    treeViewIds,
    oneChildrenIsInEditMode,
  } = useObjectInput();

  const data = reqResponse.formattedData;

  // to manage the active tab and saving state
  const [state, setState] = useState({
    activeTab: "",
    isSaving: false,
  });

  const { activeTab, isSaving } = state;

  const onStateChange = (key, value) => {
    setState({ ...state, [key]: value });
  };

  const handleSaveBtnClick = () => {
    // if the save is successful then set the isSaving to false and editMode to false
    const onSaveCb = () => {
      setState({ ...state, isSaving: false, editMode: false });
    };

    // if the save fails then set the isSaving to false and editMode to true
    const onErrorCb = () => {
      setState({ ...state, isSaving: false, editMode: true });
    };

    const requestData = createRequestData(data);
    setState({ ...state, isSaving: true });
    saveData(requestData, "", onSaveCb, onErrorCb);
  };

  const handleHideTree = () => {
    setShowCompleteTree(false);
  };

  if (!data) {
    return dataFetched ? (
      <div className="goi-no-record">
        <p>No Record Found</p>
      </div>
    ) : (
      <GOILoader />
    );
  }

  return (
    <div className={`object-input-container ${saving ? " disabled-8" : ""}`}>
      <GOIErrors />

      {/* show all the parent node names of the selected node */}
      {!showCompleteTree && (
        <div>
          <GOIParentsHeader
            parents={reqResponse?.parents}
            showCompleteTree={() => setShowCompleteTree(true)}
            key={data.id}
          />
        </div>
      )}

      {showCompleteTree ? (
        // showing hide tree button if complete tree is visible
        <>
          <div className="px-10 border-all-dark flex-end header-background">
            <span
              className="clickable py-10 hide-tree"
              onClick={handleHideTree}
            >
              hide tree
            </span>
          </div>
          <GOICompleteTree data={reqResponse.completeTree} />
        </>
      ) : (
        // showing all the children of the selected node, if complete tree is not visible
        // it will also be visble by default
        <div className="header-wrapper">
          {/* if node has children then show the special header with switch View button */}
          {data?.children && (
            <>
              <GOIHeaderSection
                activeTab={activeTab}
                setActiveTab={(value) => onStateChange("activeTab", value)}
                saving={isSaving}
                editMode={oneChildrenIsInEditMode(data.childrenIds)}
                data={data}
                handleSaveBtnClick={handleSaveBtnClick}
                showSwitchView={true}
                cancelAllowed={false}
                backToEditingAllowed={false}
              />
              {/* tab content viewer */}
              <GOIRenderTabContent activeTab={activeTab} data={data} />
            </>
          )}
          <div>
            {/* treeViewIds[data.id] will be true if the user click the switch view of selected node */}
            {!treeViewIds[data.id] ? (
              data?.associatedObjects?.length ? (
                data.associatedObjects.map((item, index) => {
                  return item?.associatedObjects?.length ? (
                    // if the associated object has children then render the first level
                    // GOIFirstLevel component shows all the children in the same level
                    <GOIFirstLevel
                      savingParentValue={isSaving}
                      item={item}
                      key={index}
                    />
                  ) : (
                    // if the associated object has no children then render the single node
                    <GOINode
                      savingParentValue={isSaving}
                      level={1}
                      inputData={item}
                      editMode={editingChildrenIds[item.id]}
                      key={index}
                    />
                  );
                })
              ) : (
                // if the selected node has no children then show the input fields
                <GOINode
                  savingParentValue={isSaving}
                  level={0}
                  inputData={data}
                  editMode={editingChildrenIds[data.id]}
                />
              )
            ) : (
              // show the tree of the selected node
              <TreeWrapper
                item={data}
                savingParentValue={isSaving}
                level={null}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GOIRoot;
